import { Star } from "lucide-react";

import { cn } from "@/lib/utils";

import { countryName } from "@/features/auth/data/countries";
import type { Worker } from "@/features/workers/types";

// --- Activity windows --------------------------------------------------------

const ONLINE_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const ACTIVE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Epoch millis for a worker's last activity (0 when missing/invalid). */
export function activityMillis(iso: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}
function activityAge(iso: string | null): number {
  const t = activityMillis(iso);
  return t === 0 ? Number.POSITIVE_INFINITY : Date.now() - t;
}
export const isOnline = (iso: string | null) =>
  activityAge(iso) <= ONLINE_WINDOW_MS;
export const isActive = (iso: string | null) =>
  activityAge(iso) <= ACTIVE_WINDOW_MS;

// --- Presentational bits -----------------------------------------------------

/** Five-star rating row, filled up to the (rounded) rating. */
export function Stars({ rating }: { rating: number }) {
  const filled = Math.round(rating);
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`Rating ${rating.toFixed(1)} of 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          aria-hidden="true"
          className={cn(
            "size-3.5",
            i < filled
              ? "fill-warning text-warning-text"
              : "fill-transparent text-muted-foreground",
          )}
        />
      ))}
    </span>
  );
}

/** Live "online" pill, shown only for very-recently-active workers. */
export function OnlineDot() {
  return (
    <span className="inline-flex items-center gap-1 text-[0.7rem] font-medium text-success-text">
      <span className="size-1.5 animate-pulse rounded-full bg-success" />
      online
    </span>
  );
}

// --- Country flag ------------------------------------------------------------

/** Turn a 2-letter ISO country code into its flag emoji (regional-indicator
 * pair). Returns "" for anything that isn't two ASCII letters, so callers can
 * fall back to the code/name. No assets, no network. */
export function flagEmoji(code: string): string {
  const cc = (code || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  const A = 0x1f1e6;
  return (
    String.fromCodePoint(A + cc.charCodeAt(0) - 65) +
    String.fromCodePoint(A + cc.charCodeAt(1) - 65)
  );
}

/** Flag glyph + accessible country name, for the worker identity cell. */
export function CountryFlag({
  code,
  className,
  showName = false,
}: {
  code: string;
  className?: string;
  showName?: boolean;
}) {
  const flag = flagEmoji(code);
  const name = countryName(code);
  if (!flag && !name) return null;
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {flag ? (
        <span aria-hidden="true" className="leading-none">
          {flag}
        </span>
      ) : null}
      {showName ? (
        <span className="truncate text-xs text-muted-foreground">{name}</span>
      ) : (
        <span className="sr-only">{name}</span>
      )}
    </span>
  );
}

// --- Leaderboard rank badge --------------------------------------------------

/** Gold / silver / bronze. Bronze keeps its own hex for the same reason the
 * podium ramps do — a medal is a ranking device, not a palette role. */
const MEDALS = ["bg-warning", "bg-muted-foreground", "bg-[#cd7f32]"] as const;

/** Ranked marker for the "Top Worker" leaderboard: gold / silver / bronze
 * medal discs for the top 3, then a plain hairline-bordered numeral. */
export function RankBadge({ rank }: { rank: number }) {
  const medal = MEDALS[rank - 1];
  return (
    <span
      className={cn(
        "inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold tabular-nums",
        medal
          ? cn(medal, "text-background shadow-sm")
          : "border border-hairline bg-elevated text-muted-foreground",
      )}
      aria-hidden="true"
    >
      {rank}
    </span>
  );
}

// --- Skill chips -------------------------------------------------------------

/** Compact skill tags with an overflow "+N" pill (worker advertised skills). */
export function SkillChips({
  skills,
  max = 3,
  className,
}: {
  skills: string[];
  max?: number;
  className?: string;
}) {
  if (skills.length === 0) return null;
  const shown = skills.slice(0, max);
  const extra = skills.length - shown.length;
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {shown.map((s) => (
        <span
          key={s}
          className="rounded-full border border-hairline bg-elevated px-2 py-0.5 text-[0.7rem] font-medium text-muted-foreground"
        >
          {s}
        </span>
      ))}
      {extra > 0 ? (
        <span className="text-[0.7rem] font-medium text-muted-foreground">
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

/** Total / successful / unsuccessful task chips (the legacy "Work Done"
 * breakdown), with hover titles. */
export function WorkDoneChips({ worker }: { worker: Worker }) {
  return (
    // Wraps rather than overflows: in the narrow stat columns (podium card and
    // the mobile ranks-4+ row) a worker with four-digit counts needs more than
    // one line, and the surrounding cards clip their overflow.
    <div className="flex flex-wrap items-center gap-1">
      <span
        title="Total tasks"
        className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs tabular-nums text-foreground"
      >
        {worker.tasksTotal}
      </span>
      <span
        title="Successful tasks"
        className="rounded-md bg-success/15 px-1.5 py-0.5 font-mono text-xs tabular-nums text-success-text"
      >
        {worker.tasksDone}
      </span>
      <span
        title="Unsuccessful tasks"
        className="rounded-md bg-destructive/15 px-1.5 py-0.5 font-mono text-xs tabular-nums text-destructive-text"
      >
        {worker.tasksNotDone}
      </span>
    </div>
  );
}
